import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, CardHeader, Form, Input, InputGroup, InputGroupAddon, InputGroupText, Media, Table } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faTags, faUserCheck } from '@fortawesome/free-solid-svg-icons'
import Loader from '../../common/Loader'
import Avatar from '../../common/Avatar'
import Pagination from '../../common/pagination/Pagination'
import useSWRFetch from '../../../helpers/hooks/useSWRFetch'
import { getAdminProviders } from '../../../helpers/api/marketplace'
import { PROVIDER_STATUS, formatIdentification, providerLocation, storageUrl } from '../../marketplace/marketplaceConstants'

const TABS = [
  { value: 'pending', label: 'Por revisar' },
  { value: 'verified', label: 'Verificados' },
  { value: 'rejected', label: 'Rechazados' },
  { value: 'suspended', label: 'Suspendidos' }
]

const RootMarketplaceProviders = () => {
  const [status, setStatus] = useState('pending')
  const [search, setSearch] = useState('')
  const [draft, setDraft] = useState('')
  const [page, setPage] = useState(1)
  const query = new URLSearchParams({ status, page: String(page), ...(search ? { search } : {}) }).toString()
  const providers = useSWRFetch(['marketplace-admin-providers', query], (key, value) => getAdminProviders(value))

  const items = providers.data?.data || []
  const meta = providers.data?.meta
  const counts = providers.data?.counts || {}

  const changeTab = (value) => {
    setStatus(value)
    setPage(1)
  }

  return (
    <Card className="mb-3">
      <CardHeader className="bg-light">
        <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 12 }}>
          <div className="mr-auto" style={{ flex: '1 1 320px', minWidth: 0 }}>
            <h5 className="mb-1">Proveedores del Marketplace</h5>
            <p className="fs--1 text-600 mb-0">Revisa los documentos de cada proveedor para darle el Sello Homelan. Solo los verificados aparecen en el Marketplace.</p>
          </div>
          <Button tag={Link} to="/root/marketplace/categories" color="falcon-default" size="sm">
            <FontAwesomeIcon icon={faTags} className="mr-1" />
            Categorías
          </Button>
        </div>
        <div className="d-flex flex-wrap align-items-center" style={{ gap: 12 }}>
          <ButtonGroup size="sm" className="flex-wrap">
            {TABS.map((tab) => (
              <Button key={tab.value} color={status === tab.value ? 'primary' : 'falcon-default'} onClick={() => changeTab(tab.value)}>
                {tab.label}
                <Badge color={status === tab.value ? 'light' : 'soft-secondary'} pill className="ml-2">
                  {counts[tab.value] ?? 0}
                </Badge>
              </Button>
            ))}
          </ButtonGroup>
          <Form
            className="ml-md-auto"
            onSubmit={(event) => {
              event.preventDefault()
              setSearch(draft.trim())
              setPage(1)
            }}
          >
            <InputGroup size="sm">
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroupText>
              </InputGroupAddon>
              <Input value={draft} onChange={({ target }) => setDraft(target.value)} placeholder="Nombre, correo, cédula o RIF" maxLength={100} />
            </InputGroup>
          </Form>
        </div>
      </CardHeader>

      {providers.error ? (
        <CardBody>
          <Alert color="danger" className="mb-0">
            No pudimos cargar los proveedores.
          </Alert>
        </CardBody>
      ) : providers.isLoading ? (
        <Loader padding="py-5" />
      ) : items.length === 0 ? (
        <CardBody className="text-center py-5">
          <FontAwesomeIcon icon={faUserCheck} size="3x" className="text-300 mb-3" />
          <p className="text-600 mb-0">
            {search ? 'No hay proveedores que coincidan con la búsqueda.' : status === 'pending' ? 'No hay proveedores esperando revisión.' : 'No hay proveedores en este estado.'}
          </p>
        </CardBody>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="fs--1 mb-0" hover>
              <thead className="bg-200 text-900">
                <tr>
                  <th>Proveedor</th>
                  <th>Contacto</th>
                  <th className="text-center">Servicios</th>
                  <th className="text-center">Documentos</th>
                  <th>Registro</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((provider) => {
                  const state = PROVIDER_STATUS[provider.status] || PROVIDER_STATUS.pending
                  const required = provider.required_documents?.length || 0
                  return (
                    <tr key={provider.id}>
                      <td style={{ minWidth: 240 }}>
                        <Link to={`/root/marketplace/providers/${provider.id}`} className="text-decoration-none">
                          <Media className="align-items-center">
                            <Avatar src={storageUrl(provider.photo)} name={provider.display_name} size="l" rounded={provider.type === 'company' ? 'soft' : 'circle'} className="mr-2" />
                            <Media body>
                              <span className="font-weight-semi-bold text-900 d-block">{provider.display_name}</span>
                              <span className="text-600 d-block">
                                {provider.type === 'company'
                                  ? `Empresa · ${formatIdentification(provider.rif, true) || ''}`
                                  : `Persona natural · ${formatIdentification(provider.dni) || ''}`}
                              </span>
                              <span className="text-600">{providerLocation(provider) || provider.address}</span>
                            </Media>
                          </Media>
                        </Link>
                      </td>
                      <td>
                        <span className="d-block">{provider.email}</span>
                        <span className="text-600">{provider.phone}</span>
                      </td>
                      <td className="text-center">{provider.services_count ?? 0}</td>
                      <td className="text-center">
                        <span className={provider.documents_count < required ? 'text-danger font-weight-semi-bold' : ''}>
                          {provider.documents_count ?? 0}/{required}
                        </span>
                      </td>
                      <td className="text-nowrap">{moment(provider.created_at).format('DD/MM/YYYY')}</td>
                      <td className="text-nowrap">
                        <Badge color={`soft-${state.color}`} pill>
                          {state.label}
                        </Badge>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
          {meta && meta.last_page > 1 && (
            <CardBody className="d-flex justify-content-center py-3">
              <Pagination
                meta={{ currentPage: meta.current_page, lastPage: meta.last_page }}
                prevPage={() => setPage(meta.current_page - 1)}
                nextPage={() => setPage(meta.current_page + 1)}
                goToPage={setPage}
              />
            </CardBody>
          )}
        </>
      )}
    </Card>
  )
}

export default RootMarketplaceProviders
